import React, { useEffect, useRef, useMemo } from 'react';
import { headerContent, headerPhaseContent } from '@/content';
import type { LaunchPhase } from '@/content/types';
import { createSmoothScrollHandler } from '@/utils/scrollToElement';
import { getViteMetaEnv } from '@/utils/viteMetaEnv';
import styles from './Header.module.scss';
import Logo from '@/assets/logo/logo';
import Wordmark from '@/assets/logo/wordmark';

export const Header: React.FC = () => {
  const headerRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  // Removed scrollTimeoutRef as manual will-change optimization was causing flicker

  // Smoothly transition logo position based on scroll
  useEffect(() => {
    const spacer = spacerRef.current;
    const header = headerRef.current;
    const logo = document.querySelector(`.${styles.logo}`) as HTMLElement;
    if (!spacer || !header || !logo) return;

    // CRITICAL FIX: Apply 3D transform immediately to promote logo to GPU layer
    // Use non-zero Z (0.001px) to force GPU promotion - browser optimizes Z=0 to 2D!
    logo.style.transform = 'translate3d(0, 0, 0.001px)';

    // Read CSS variables as computed pixel values
    // Create a temporary element to convert CSS custom properties to pixels
    const tempDiv = document.createElement('div');
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.position = 'absolute';
    tempDiv.style.height = 'var(--size-40)';
    document.body.appendChild(tempDiv);
    const logoStartTop = parseFloat(getComputedStyle(tempDiv).height);
    tempDiv.style.height = 'var(--size-16)';
    const logoEndTop = parseFloat(getComputedStyle(tempDiv).height);
    document.body.removeChild(tempDiv);
    
    // Initial position for transform calculation
    const initialTop = logoStartTop;
    
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Get header's current position relative to viewport
          const headerRect = header.getBoundingClientRect();
          const headerTop = headerRect.top;
          
          // Logo should mirror the header's vertical position, but clamped to our range
          let logoTop = logoStartTop;
          
          if (headerTop <= logoStartTop && headerTop >= logoEndTop) {
            // Header is in transition zone - logo follows it exactly
            logoTop = headerTop;
          } else if (headerTop < logoEndTop) {
            // Header is stuck at its minimum - logo also at minimum
            logoTop = logoEndTop;
          }
          
          // Use GPU-accelerated transform instead of layout-triggering top property
          // Non-zero Z (0.001px) forces GPU layer - browser optimizes Z=0 to 2D!
          const translateY = logoTop - initialTop;
          logo.style.transform = `translate3d(0, ${translateY}px, 0.001px)`;

          ticking = false;
        });
        ticking = true;
      }
    };

    // Initial calculation
    handleScroll();

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const launchPhase = (getViteMetaEnv().VITE_LAUNCH_MODE || 'stable') as LaunchPhase;
  const phaseCta = useMemo(() => {
    const phaseKeyMap: Record<LaunchPhase, keyof typeof headerPhaseContent> = {
      'pre-launch': 'preLaunch',
      'beta': 'beta',
      'early-bird': 'earlyBird',
      'stable': 'stable',
    };
    return headerPhaseContent[phaseKeyMap[launchPhase]].cta;
  }, [launchPhase]);

  const handleSmoothScroll = createSmoothScrollHandler();

  return (
    <>
      {/* Fixed logo - always visible at top-left */}
      <div className={`${styles["logo"]}`}>
        <a 
          href="/" 
          className={`${styles["logo__link"]}`}
          aria-label="Return to top - colorscales.io"
        >
          <div className={`${styles["logo__container"]}`}>
            <Logo />
          </div>
          <Wordmark className={`${styles["logo__wordmark"]}`} />
        </a>
      </div>

      {/* Spacer to push nav down to ~80vh initially */}
      <div ref={spacerRef} className={`hide--mobile ${styles["header__spacer"]}`} aria-hidden="true" />

      {/* Navigation - scrolls naturally with page, sticks when reaching top */}
      <header ref={headerRef} className={styles.header} role="banner">
        <nav ref={navRef} className={`hide--mobile ${styles["header__nav"]}`} role="navigation" aria-label="Main navigation">
          <ul className={styles.header__navList}>
            {headerContent.navigation.links.map((link, index) => (
              <li key={index}>
                <a
                  href={`#${link.targetId}`}
                  onClick={handleSmoothScroll}
                  className={styles.header__navLink}
                >
                  {link.text}
                </a>
              </li>
            ))}
          </ul>
          <a
            href={`#${phaseCta.targetId}`}
            onClick={handleSmoothScroll}
            className={styles.header__navButton}
          >
            {phaseCta.text}
          </a>
        </nav>
      </header>
    </>
  );
};

